import React, { useState, useEffect, useMemo } from 'react';
import styled from "@emotion/styled";
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery
} from "@mui/material";
import { TabPanel } from '@mui/lab';
import { Clear, OpenInNew, Refresh, Search } from '@mui/icons-material';
import { getUnconfiguredFixedPriceEntities } from '../redux/ducks/fixedPricePayments';
import { formatDateTime } from '../utils/dateUtils';

const SetupTabPanel = styled(TabPanel)(({ theme }) => ({
    padding: 0,
    paddingTop: theme.spacing(3),
}));

const ToolbarRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
}));

const SearchField = styled(TextField)(() => ({
    flex: 1,
    minWidth: 220,
}));

const CenteredLoading = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
}));

const TypeChip = styled(Chip)(({ theme }) => ({
    height: 20,
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightSemiBold,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    marginRight: theme.spacing(1),
}));

const EmptyTableCell = styled(TableCell)(({ theme }) => ({
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
}));

const MobileCardList = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
}));

const EntityRowCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(1.5),
}));

const CardTopRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
}));

const CardMetaRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.75),
    flexWrap: 'wrap',
}));

const CardFooter = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(0.5),
}));

const EmptyMobileState = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4, 2),
    textAlign: 'center',
}));

const formatCreated = (date) => formatDateTime(date) || '—';

const formatCurrency = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const BillingFixedPriceSetupNeeded = ({ tabValue }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const [entities, setEntities] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    const loadEntities = () => {
        setIsLoading(true);
        dispatch(getUnconfiguredFixedPriceEntities((response) => {
            setIsLoading(false);
            setEntities(response?.entities || []);
        }));
    };

    useEffect(() => {
        if (tabValue === 'setup-needed') {
            loadEntities();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue]);

    const filtered = useMemo(() => {
        if (!search) return entities;
        return entities.filter((e) => {
            const haystack = [
                e.client?.name,
                e.account?.name,
                e.parent?.name
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(search);
        });
    }, [entities, search]);

    const openEntity = (row) => {
        if (!row?.parent || !row?.account?.slug) return;
        if (row.parent.type === 'Project') {
            navigate(`/account/${row.account.slug}/project/${row.parent._id}`);
        } else if (row.parent.type === 'Group') {
            navigate(`/account/${row.account.slug}`);
        }
    };

    const openTooltip = (row) => {
        if (!row?.account?.slug) return 'No account link available';
        if (row?.parent?.type === 'Group') return 'Open the Account page to manage this group\'s payments';
        return 'Open to configure payments';
    };

    return (
        <SetupTabPanel value="setup-needed">
            <Card>
                <CardHeader
                    title="Setup Needed"
                    subheader="Billable Fixed Price projects and groups that have no payment schedule configured and are not yet completed"
                    action={
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Refresh />}
                            onClick={loadEntities}
                            disabled={isLoading}
                        >
                            Refresh
                        </Button>
                    }
                />
                <CardContent>
                    <ToolbarRow>
                        <SearchField
                            size="small"
                            placeholder="Search by client, account, or name"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: searchInput ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchInput('')}>
                                            <Clear fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null
                            }}
                        />
                    </ToolbarRow>

                    {isLoading ? (
                        <CenteredLoading>
                            <CircularProgress />
                        </CenteredLoading>
                    ) : isMobile ? (
                        filtered.length === 0 ? (
                            <EmptyMobileState>
                                <Typography variant="body2" color="text.secondary">
                                    All Fixed Price projects and groups have at least one scheduled payment.
                                </Typography>
                            </EmptyMobileState>
                        ) : (
                            <MobileCardList>
                                {filtered.map((row) => {
                                    const rowKey = `${row.parent.type}-${row.parent._id}`;
                                    return (
                                        <EntityRowCard key={rowKey} variant="outlined">
                                            <CardTopRow>
                                                <Box minWidth={0}>
                                                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
                                                        <TypeChip
                                                            label={row.parent?.type}
                                                            size="small"
                                                            variant="outlined"
                                                            color={row.parent?.type === "Project" ? "primary" : "secondary"}
                                                        />
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {row.parent?.name || '—'}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {row.client?.name || '—'}
                                                        {row.account?.name && row.account.name !== row.client?.name ? ` · ${row.account.name}` : ''}
                                                    </Typography>
                                                </Box>
                                            </CardTopRow>
                                            <CardMetaRow>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Expected total
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {formatCurrency(row.parent?.totalPayment)}
                                                    </Typography>
                                                </Box>
                                                <Box textAlign="right">
                                                    <Typography variant="caption" color="text.secondary">
                                                        Created
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {formatCreated(row.createdAt)}
                                                    </Typography>
                                                </Box>
                                            </CardMetaRow>
                                            <CardFooter>
                                                <Tooltip title={openTooltip(row)}>
                                                    <span>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<OpenInNew fontSize="small" />}
                                                            onClick={() => openEntity(row)}
                                                            disabled={!row.account?.slug}
                                                        >
                                                            Open
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            </CardFooter>
                                        </EntityRowCard>
                                    );
                                })}
                            </MobileCardList>
                        )
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Client</TableCell>
                                        <TableCell>Account</TableCell>
                                        <TableCell align="right">Expected total</TableCell>
                                        <TableCell>Created</TableCell>
                                        <TableCell align="right">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filtered.length === 0 ? (
                                        <TableRow>
                                            <EmptyTableCell colSpan={7} align="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    All Fixed Price projects and groups have at least one scheduled payment.
                                                </Typography>
                                            </EmptyTableCell>
                                        </TableRow>
                                    ) : (
                                        filtered.map((row) => {
                                            const rowKey = `${row.parent.type}-${row.parent._id}`;
                                            return (
                                                <TableRow key={rowKey} hover>
                                                    <TableCell>
                                                        <TypeChip
                                                            label={row.parent?.type}
                                                            size="small"
                                                            variant="outlined"
                                                            color={row.parent?.type === "Project" ? "primary" : "secondary"}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {row.parent?.name || '—'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {row.client?.name || '—'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {row.account?.name || '—'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrency(row.parent?.totalPayment)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {formatCreated(row.createdAt)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title={openTooltip(row)}>
                                                            <span>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    startIcon={<OpenInNew fontSize="small" />}
                                                                    onClick={() => openEntity(row)}
                                                                    disabled={!row.account?.slug}
                                                                >
                                                                    Open
                                                                </Button>
                                                            </span>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
        </SetupTabPanel>
    );
};

export default BillingFixedPriceSetupNeeded;
